import axios, { AxiosRequestConfig } from 'axios'
import { Host } from '@/host'
import { cms } from '@/cms'

export class RestError extends Error {
  status: number | null
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'RestError'
    this.status = status || null
  }
}

interface RequestOptions {
  headers?: Record<string, string>
  params?: Record<string, string>
  body?: any
  withCmsAuth?: boolean
}

export class RestBase {
  private host: string = ''
  private apiKey: string = ''

  constructor(apiKey: string = '') {
    this.host = new Host().rest
    this.apiKey = apiKey
  }

  public bearerAuthorizationHeader(token: string|null): Record<string, string> {
    if (!token) {
      return {} // Never create empty authorization header
    }
    return { Authorization: `Bearer ${token}` }
  }

  public async request(method: 'GET'|'POST'|'PUT'|'DELETE'|'PATCH', url: string, options: RequestOptions = {}): Promise<any> {
    const headers = options.headers || {}
    const withCmsAuth = options.withCmsAuth || false
    if (withCmsAuth && cms.accessToken) {
      // Only set Authorization when cms is authorized, never set empty Authorization header
      headers.Authorization = cms.accessToken
    }

    // url can be either absolute or path only url, in case of relative url we add the host
    const urlObj = new URL(url, this.host)
    if (options.params) {
      for (const [key, value] of Object.entries(options.params)) {
        urlObj.searchParams.append(key, value)
      }
    }
    // axios request config
    const requestConfig: AxiosRequestConfig = {
      method,
      url: urlObj.toString(),
      headers,
    }
    if (options.body) {
      requestConfig.data = options.body
    }
    try {
      const resp = await axios.request(requestConfig)
      return resp.data
    } catch (err) {
      throw new RestError(
        JSON.stringify({
          message: (err as any).response?.data, headers: (err as any).response?.headers, url: urlObj.toString(),
        }),
        (err as any).response?.status
      )
    }
  }

  public async get(url: string, options: RequestOptions = {}): Promise<any> {
    return await this.request('GET', url, options)
  }

  public async post(url: string, options: RequestOptions = {}): Promise<any> {
    return await this.request('POST', url, options)
  }

  public async put(url: string, options: RequestOptions = {}): Promise<any> {
    return await this.request('PUT', url, options)
  }

  public async delete(url: string, options: RequestOptions = {}): Promise<any> {
    return await this.request('DELETE', url, options)
  }

  public async patch(url: string, options: RequestOptions = {}): Promise<any> {
    return await this.request('PATCH', url, options)
  }
}
